import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, MessageCircle, Phone, Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Stars, Money, Chip } from "@/components/ui-veda/Primitives";
import { TID } from "@/constants/testIds";

function buildSlots() {
    const out = [];
    const now = new Date();
    for (let d = 0; d < 5; d++) {
        const day = new Date(now);
        day.setDate(now.getDate() + d);
        ["10:00", "12:00", "15:00", "17:30"].forEach((t) => {
            const label = day.toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
                weekday: "short",
            });
            out.push({
                key: `${day.toISOString().slice(0, 10)} ${t}`,
                dayLabel: label,
                timeLabel: t,
            });
        });
    }
    return out;
}

export default function DoctorDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const { requireAuth, user } = useAuth();
    const [d, setD] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [reviews, setReviews] = useState([]);
    const [mode, setMode] = useState("chat"); // 'chat' | 'call'
    const [slot, setSlot] = useState("");
    const [notes, setNotes] = useState("");
    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState("");
    const [booking, setBooking] = useState(false);

    useEffect(() => {
        setNotFound(false);
        api.get(`/doctors/${id}`)
            .then((r) => setD(r.data))
            .catch((err) => {
                if (err?.response?.status === 404) setNotFound(true);
                else console.error(err);
            });
        api.get(`/doctors/${id}/reviews`)
            .then((r) => setReviews(r.data))
            .catch(() => {});
    }, [id]);

    const slots = useMemo(() => buildSlots(), []);

    async function book() {
        if (!slot) {
            toast.error("Pick a time slot first.");
            return;
        }
        setBooking(true);
        try {
            const { data } = await api.post("/consultations", {
                doctor_id: id,
                slot,
                mode,
                notes: notes.trim() || null,
            });
            toast.success(
                mode === "call"
                    ? "Booked. The Vaidya will call you at the slot."
                    : "Consultation booked. Opening chat...",
            );
            nav(`/consultation/${data.consultation_id}`);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Please try again.");
        } finally {
            setBooking(false);
        }
    }

    async function submitReview() {
        if (!rating) {
            toast.error("Pick a star rating first.");
            return;
        }
        try {
            await api.post(`/doctors/${id}/reviews`, {
                rating,
                comment: comment.trim(),
            });
            toast.success("Review posted.");
            setComment("");
            setRating(0);
            const r = await api.get(`/doctors/${id}/reviews`);
            setReviews(r.data);
            const dx = await api.get(`/doctors/${id}`);
            setD(dx.data);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Please try again.");
        }
    }

    if (notFound) {
        return (
            <div className="veda-container py-24 text-center">
                <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-veda-muted">
                    404
                </p>
                <h1 className="font-display mt-2 text-4xl text-veda-onSurface">
                    Vaidya not found
                </h1>
                <p className="mt-3 text-sm text-veda-muted">
                    This practitioner isn't in our directory.
                </p>
                <Link
                    to="/"
                    className="mt-6 inline-block bg-veda-inverse px-5 py-3 text-xs font-semibold uppercase tracking-[0.16em] text-veda-onInverse"
                >
                    Browse Vaidyas
                </Link>
            </div>
        );
    }
    if (!d) {
        return (
            <div className="p-8 text-center text-sm text-veda-muted">Loading...</div>
        );
    }

    return (
        <div className="pb-32 md:pb-16">
            <div className="veda-container pt-6">
                <Link
                    to="/"
                    className="inline-flex items-center gap-1 text-xs font-medium text-veda-muted hover:text-veda-onSurface"
                >
                    <ArrowLeft size={14} /> Back
                </Link>
            </div>

            <div className="veda-container mt-6 grid gap-10 md:grid-cols-[1fr_1.3fr] md:gap-14">
                {/* Left: photo + about (sticky on desktop) */}
                <div className="md:sticky md:top-24 md:self-start">
                    <div className="grid grid-cols-[auto_1fr] gap-4 md:grid-cols-1">
                        <img
                            src={d.avatar_url}
                            alt={d.name}
                            className="h-28 w-28 object-cover md:h-full md:w-full md:aspect-square"
                        />
                        <div className="md:mt-6">
                            <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-veda-brand">
                                {d.specialty}
                            </p>
                            <h1 className="font-display mt-1 text-2xl text-veda-onSurface md:text-4xl">
                                {d.name}
                            </h1>
                            <div className="mt-2 flex items-center gap-2">
                                <Stars value={d.rating || 0} size={12} />
                                <span className="text-xs text-veda-muted">
                                    {d.rating_count || 0} reviews · {d.experience_years}y exp
                                </span>
                            </div>
                            <p className="mt-3 text-sm font-semibold text-veda-onSurface md:text-base">
                                <Money value={d.consultation_fee} /> · per consultation
                            </p>
                        </div>
                    </div>

                    {d.bio && (
                        <div className="mt-6 border-t border-veda-divider pt-5">
                            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                                About
                            </p>
                            <p className="mt-2 text-sm leading-relaxed text-veda-onSurface/85">
                                {d.bio}
                            </p>
                        </div>
                    )}
                    {d.qualifications && (
                        <div className="mt-5 border-t border-veda-divider pt-5">
                            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                                Qualifications
                            </p>
                            <p className="mt-2 text-sm leading-relaxed text-veda-onSurface/85">
                                {d.qualifications}
                            </p>
                        </div>
                    )}
                </div>

                {/* Right: booking + reviews */}
                <div>
                    <div className="border border-veda-border bg-veda-surface p-5 md:p-7">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                            Consultation mode
                        </p>
                        <div className="mt-2 flex gap-2">
                            <button
                                onClick={() => setMode("chat")}
                                className={`inline-flex items-center gap-2 border px-3 py-2 text-xs font-semibold uppercase tracking-[0.14em] ${
                                    mode === "chat"
                                        ? "border-veda-inverse bg-veda-inverse text-veda-onInverse"
                                        : "border-veda-border text-veda-onSurface"
                                }`}
                            >
                                <MessageCircle size={13} /> Chat
                            </button>
                            <button
                                onClick={() => setMode("call")}
                                className={`inline-flex items-center gap-2 border px-3 py-2 text-xs font-semibold uppercase tracking-[0.14em] ${
                                    mode === "call"
                                        ? "border-veda-inverse bg-veda-inverse text-veda-onInverse"
                                        : "border-veda-border text-veda-onSurface"
                                }`}
                            >
                                <Phone size={13} /> Call
                            </button>
                        </div>

                        <p className="mt-6 text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                            Pick a slot
                        </p>
                        <div className="no-scrollbar mt-2 flex gap-2 overflow-x-auto pb-2">
                            {slots.map((s) => (
                                <button
                                    key={s.key}
                                    onClick={() => setSlot(s.key)}
                                    className={`min-w-[110px] shrink-0 border p-2 text-left ${
                                        slot === s.key
                                            ? "border-veda-brand bg-veda-brand/10"
                                            : "border-veda-border bg-veda-surface"
                                    }`}
                                >
                                    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-muted">
                                        {s.dayLabel}
                                    </p>
                                    <p className="mt-1 text-sm font-semibold text-veda-onSurface">
                                        {s.timeLabel}
                                    </p>
                                </button>
                            ))}
                        </div>

                        <textarea
                            rows={3}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Anything the Vaidya should know beforehand? (optional)"
                            className="mt-4 w-full border border-veda-border bg-veda-surface p-3 text-sm focus:border-veda-brand focus:outline-none"
                        />

                        {/* Desktop Book button */}
                        <button
                            data-testid={`${TID.bookConsultationBtn}-desktop`}
                            onClick={() => requireAuth(book)}
                            disabled={booking}
                            className="mt-5 hidden w-full items-center justify-center gap-2 bg-veda-inverse px-4 py-3 text-sm font-semibold uppercase tracking-[0.16em] text-veda-onInverse disabled:opacity-70 md:inline-flex"
                        >
                            {booking && <Loader2 size={14} className="animate-spin" />}
                            Book consultation · <Money value={d.consultation_fee} />
                        </button>
                    </div>

                    <div className="mt-8 border-t border-veda-divider pt-6">
                        <p className="font-display text-2xl text-veda-onSurface">
                            Patient reviews
                        </p>
                        <div className="mt-3 border border-veda-border bg-veda-surface p-4">
                            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                                Leave a review
                            </p>
                            <div className="mt-2 flex items-center gap-1">
                                {[1, 2, 3, 4, 5].map((n) => (
                                    <button
                                        key={n}
                                        onClick={() => requireAuth(() => setRating(n))}
                                        aria-label={`Rate ${n} stars`}
                                    >
                                        <Star
                                            size={20}
                                            strokeWidth={1.5}
                                            className={
                                                n <= rating
                                                    ? "fill-veda-warning text-veda-warning"
                                                    : "text-veda-borderStrong"
                                            }
                                        />
                                    </button>
                                ))}
                            </div>
                            <textarea
                                rows={2}
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                placeholder="Share your experience..."
                                className="mt-3 w-full border border-veda-border bg-veda-surface p-3 text-sm"
                            />
                            <button
                                onClick={() => requireAuth(submitReview)}
                                className="mt-3 bg-veda-brand px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-white"
                            >
                                Post
                            </button>
                        </div>
                        <ul className="mt-4 divide-y divide-veda-divider">
                            {reviews.length === 0 && (
                                <li className="py-4 text-sm text-veda-muted">
                                    No reviews yet.
                                </li>
                            )}
                            {reviews.map((r) => (
                                <li key={r.review_id} className="py-4">
                                    <div className="flex items-center justify-between">
                                        <p className="text-sm font-semibold text-veda-onSurface">
                                            {r.user_name}
                                        </p>
                                        <Stars value={r.rating} size={11} />
                                    </div>
                                    {r.comment && (
                                        <p className="mt-1 text-sm text-veda-onSurface/80">
                                            {r.comment}
                                        </p>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>

            {/* Mobile sticky bottom CTA */}
            <div className="fixed inset-x-0 bottom-0 z-20 border-t border-veda-border bg-veda-surface md:hidden">
                <div className="veda-container py-3">
                    <button
                        data-testid={TID.bookConsultationBtn}
                        onClick={() => requireAuth(book)}
                        disabled={booking}
                        className="inline-flex w-full items-center justify-center gap-2 bg-veda-inverse px-4 py-3 text-sm font-semibold uppercase tracking-[0.16em] text-veda-onInverse disabled:opacity-70"
                    >
                        {booking && <Loader2 size={14} className="animate-spin" />}
                        Book consultation · <Money value={d.consultation_fee} />
                    </button>
                </div>
            </div>
        </div>
    );
}
